import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Shield,
  Calendar,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Loader2,
  PlaneTakeoff,
  Clock,
  Check,
  XCircle,
  RefreshCw,
  TrendingDown,
  AlertCircle,
  Wallet,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders, mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { User } from '@/api/entities';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { format, parseISO, differenceInDays } from 'date-fns';
import { getCityName } from '@/components/airportUtils';
import FlightOfferCard from '@/components/search/FlightOfferCard';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { logReserveActivity, newReserveAttemptId } from '@/lib/reserveActivityLog';
import { resolveMarketingFareNameFromOffer } from '@/utils/marketingFareName';
import { buildTommyUrl } from '@/constants/support';

function PaymentCalculator({
  totalCents,
  baseFareCents,
  serviceFeeCents,
  departureDate,
  onConfirm,
  isProcessing,
  availableBalanceCents = 0,
  errorMessage = null,
  onRetry = null,
  supportHref = null,
  children,
}) {
  const [frequency, setFrequency] = useState('weekly');
  const [showFlightDetails, setShowFlightDetails] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [acknowledgedReserveTerms, setAcknowledgedReserveTerms] = useState(false);

  // Cap the applicable balance at totalCents - $1 — Stripe needs at least one chargeable
  // installment to actually create the subscription (a $0 plan can't kick off the recurring
  // schedule), so we never let "Apply" zero out the trip from the calculator.
  const maxApplicableCents = Math.max(0, Math.min(availableBalanceCents || 0, Math.max(0, totalCents - 100)));
  const hasApplicableBalance = maxApplicableCents > 0;
  const [applyBalance, setApplyBalance] = useState(false);
  const appliedBalanceCents = applyBalance ? maxApplicableCents : 0;
  const effectiveTotalCents = Math.max(0, totalCents - appliedBalanceCents);

  // If the available balance changes after toggle was set (e.g. user opened a new tab and
  // spent some), keep the toggle conservative: clear it whenever the cap drops to 0.
  useEffect(() => {
    if (!hasApplicableBalance && applyBalance) setApplyBalance(false);
  }, [hasApplicableBalance, applyBalance]);

  const schedule = useMemo(() => {
    const now = new Date();
    const dep = new Date(departureDate);
    const deadline = new Date(dep);
    deadline.setDate(deadline.getDate() - 28);
    const daysAvailable = Math.max(1, differenceInDays(deadline, now));

    const intervals = { weekly: 7, biweekly: 14, monthly: 30 };
    const intervalDays = intervals[frequency];
    const numPayments = Math.max(1, 1 + Math.floor(daysAvailable / intervalDays));

    const dates = [new Date(now)];
    for (let i = 1; i < numPayments; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() + intervalDays * i);
      if (d <= deadline) dates.push(d);
    }

    const count = dates.length;
    // The schedule (and thus today's first charge) is computed from the post-credit total
    // when the user opted to apply Reserve balance. The full trip total is preserved on the
    // server for accounting; only the subscription's installment math uses this number.
    const base = Math.floor(effectiveTotalCents / count);
    const remainder = effectiveTotalCents - base * count;

    return {
      numPayments: count,
      perPaymentCents: base,
      lastPaymentCents: base + remainder,
      dates,
      deadline,
      daysAvailable
    };
  }, [effectiveTotalCents, departureDate, frequency]);

  const formatCurrency = (cents) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const frequencyLabel = { weekly: 'Weekly', biweekly: 'Every 2 Weeks', monthly: 'Monthly' };

  return (
    <div className="space-y-6">
      {/* Due Today - prominent */}
      <div className="text-center py-6">
        <p className="text-white/40 text-xs tracking-widest uppercase mb-3">Due Today</p>
        <p className="text-5xl font-light text-white tracking-tight">${formatCurrency(schedule.perPaymentCents)}</p>
        <div className="flex items-center justify-center gap-2 mt-3">
          <p className="text-white/40 text-sm font-normal">Base Fare: ${formatCurrency(baseFareCents)}</p>
        </div>
      </div>

      {/* Frequency Selector */}
      <div>
        <p className="text-white/40 text-xs tracking-widest uppercase mb-3">Payment Frequency</p>
        <div className="grid grid-cols-3 gap-2">
          {['weekly', 'biweekly', 'monthly'].map((f) => {
            const isSelected = frequency === f;
            return (
              <button
                key={f}
                onClick={() => setFrequency(f)}
                className={`relative py-3 px-4 rounded-xl text-sm font-normal transition-all
                  ${isSelected
                    ? 'bg-white text-black'
                    : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/20'
                  }`}
              >
                {frequencyLabel[f]}
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Calculator Result */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white text-2xl font-light">${formatCurrency(schedule.perPaymentCents)}</p>
            <p className="text-white/40 text-xs font-normal">{frequencyLabel[frequency].toLowerCase()}</p>
          </div>
          <div className="text-right">
            <p className="text-white/85 text-sm font-normal">{schedule.numPayments} payments</p>
            <p className="text-white/40 text-xs font-normal">
              finishing by {format(schedule.deadline, 'MMM d, yyyy')}
            </p>
          </div>
        </div>

        {/* Timeline Preview */}
        <div className="relative">
          <button
            onClick={() => setShowFlightDetails(!showFlightDetails)}
            className="w-full flex items-center justify-between py-2 text-xs text-white/40 hover:text-white/60 transition-colors"
          >
            <span>View payment schedule</span>
            {showFlightDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showFlightDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="space-y-2 pt-2 border-t border-white/5">
                {schedule.dates.map((date, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white/50">
                        {i + 1}
                      </div>
                      <span className="text-white/60 text-sm font-normal">{i === 0 ? 'Today' : format(date, 'MMM d, yyyy')}</span>
                    </div>
                    <span className="text-white/85 text-sm font-normal">${formatCurrency(i === schedule.dates.length - 1 ? schedule.lastPaymentCents : schedule.perPaymentCents)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Info pills */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <Shield className="w-3 h-3 text-emerald-400" />
          No large deposits
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <Clock className="w-3 h-3 text-emerald-400" />
          No interest
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <Calendar className="w-3 h-3 text-emerald-400" />
          Equal payments
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <XCircle className="w-3 h-3 text-emerald-400" />
          Cancel anytime
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <RefreshCw className="w-3 h-3 text-emerald-400" />
          Switch flights anytime
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/50">
          <TrendingDown className="w-3 h-3 text-emerald-400" />
          Pay lower if prices go down
        </div>
      </div>

      {/* Apply Reserve balance toggle. Only shown when the customer has unallocated Reserve
          credit they could put toward this trip. We don't allow zeroing the trip out (cap is
          totalCents - $1) because Stripe needs at least one chargeable installment to set up
          the recurring schedule. */}
      {hasApplicableBalance && (
        <label className={`flex items-start gap-3 rounded-2xl border ${applyBalance ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-white/[0.02]'} p-4 cursor-pointer transition-colors`}>
          <input
            type="checkbox"
            checked={applyBalance}
            onChange={(e) => setApplyBalance(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0 cursor-pointer"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Wallet className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-white text-sm font-medium">
                Apply ${formatCurrency(maxApplicableCents)} from your Reserve balance
              </span>
            </div>
            <p className="text-white/60 text-[12.5px] font-normal leading-relaxed">
              {applyBalance
                ? `Reduces your installments to $${formatCurrency(schedule.perPaymentCents)} each. Your balance will be allocated to this trip and applied at ticketing.`
                : 'Lower today\'s payment and every payment after by using credit you\'ve already added to Reserve.'}
            </p>
          </div>
        </label>
      )}

      {/* Price Summary */}
      {totalCents > 0 && (
        <div className="space-y-2 py-4 border-t border-white/5">
          <div className="flex items-center justify-between">
            <span className="text-white/30 text-xs font-normal">Trip total</span>
            <span className="text-white/30 text-xs font-normal">${formatCurrency(totalCents)}</span>
          </div>
          {appliedBalanceCents > 0 && (
            <>
              <div className="flex items-center justify-between mt-1">
                <span className="text-emerald-400/80 text-xs font-normal">From your Reserve balance</span>
                <span className="text-emerald-400/80 text-xs font-normal">−${formatCurrency(appliedBalanceCents)}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-white/30 text-xs font-normal">Remaining to schedule</span>
                <span className="text-white/30 text-xs font-normal">${formatCurrency(effectiveTotalCents)}</span>
              </div>
            </>
          )}
          {schedule.dates?.length > 0 && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-emerald-400 text-xs font-normal">Due today</span>
              <span className="text-emerald-400 text-xs font-normal">${formatCurrency(schedule.perPaymentCents)}</span>
            </div>
          )}
          {schedule.dates?.length > 1 && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-white/40 text-xs font-normal">Next payment</span>
              <span className="text-white/40 text-xs font-normal">
                {format(schedule.dates[1], 'MMM d, yyyy')} · ${formatCurrency(schedule.perPaymentCents)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="pt-4">
        {children}
      </div>

      {/* Acknowledgement */}
      <label className="flex items-start gap-3 pt-4 pb-2 border-t border-white/5 cursor-pointer group">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-white/65 text-[12.5px] leading-relaxed font-normal">
          I acknowledge that payments under this Reserve plan are converted to Defera Credits. Unticketed trip amounts remain as Defera Credits in my account and are non-withdrawable, non-refundable to the original payment method, and have no cash value. Defera Credits do not expire. Credits may only be applied toward eligible travel on Defera. I understand that this flight can be canceled at any time before ticketing; upon cancellation, any paid amounts remain as Defera Credits in my account.
        </span>
      </label>

      {/* Reserve trip & price disclosure */}
      <label className="flex items-start gap-3 pt-0 pb-4 cursor-pointer group">
        <input
          type="checkbox"
          checked={acknowledgedReserveTerms}
          onChange={(e) => setAcknowledgedReserveTerms(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/50 focus:ring-offset-0 cursor-pointer"
        />
        <span className="text-white/65 text-[12.5px] leading-relaxed font-normal">
          I understand that reserving a flight does not lock in my trip or the fare price. Both availability and pricing may change until I ticket. I cannot ticket until I have paid off my plan in full or topped off my balance to expedite.
        </span>
      </label>

      {/* Inline error block — replaces the bare toast that used to be the only feedback when
          /api/reserve/create-trip failed. We surface the actual error, an explicit retry, and
          a one-tap support fallback so customers never end up at the "I tried Reserve and
          something happened" dead end that sent Nevaeh manually adding $54 via Add Funds. */}
      {errorMessage && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-100 leading-snug">
              <p className="font-medium">We couldn&apos;t start your Reserve plan.</p>
              <p className="text-red-200/80 font-normal mt-1">{errorMessage}</p>
              <p className="text-red-200/60 text-xs font-normal mt-2">
                Your card has not been charged. You can try again, or text our team and we&apos;ll get you set up.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {onRetry && (
              <Button
                type="button"
                onClick={onRetry}
                disabled={isProcessing}
                className="flex-1 bg-white text-black hover:bg-white/90 rounded-full h-10 text-sm font-medium"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-2" />
                Try again
              </Button>
            )}
            {supportHref && (
              <a
                href={supportHref}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full h-10 text-sm font-medium border border-white/20 text-white hover:bg-white/10 transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Text support
              </a>
            )}
          </div>
        </div>
      )}

      <Button
        onClick={() => onConfirm(frequency, schedule, { applyBalanceCents: appliedBalanceCents })}
        disabled={isProcessing || !acknowledged || !acknowledgedReserveTerms}
        className="w-full bg-white text-black hover:bg-white/90 h-14 text-lg font-medium rounded-full disabled:opacity-50 disabled:pointer-events-none"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Setting up payments...
          </>
        ) : (
          <>
            Start Reserve Plan
            <CreditCard className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>

      <p className="text-white/25 text-[10px] text-center leading-relaxed">
        By continuing, you agree to the <Link to="/reserve/terms" className="underline hover:text-white/40">Defera Reserve Terms</Link>.
        Credits are non-withdrawable and have no cash value. Pricing remains dynamic until ticketing.
      </p>
    </div>
  );
}

function PaymentMethodSelector({ paymentMethods, selectedId, onSelect, onAddNew }) {
  if (paymentMethods.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-6">
        <CreditCard className="w-8 h-8 text-white/20 mb-3" />
        <p className="text-white/60 text-sm font-normal mb-4">Add a card to get started</p>
        <Button
          onClick={onAddNew}
          className="bg-white text-black hover:bg-white/90 rounded-full"
        >
          Add Card
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Payment Method</p>
      <div className="space-y-2.5">
        {paymentMethods.map((pm) => (
          <button
            key={pm.id}
            type="button"
            onClick={() => onSelect(pm.id)}
            className={`${paymentCardGradient.cardRowBase} ${
              selectedId === pm.id ? paymentCardGradient.cardSelected : paymentCardGradient.cardUnselected
            }`}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
              selectedId === pm.id ? paymentCardGradient.iconSelected : paymentCardGradient.iconUnselected
            }`}>
              <CreditCard className={`w-5 h-5 ${selectedId === pm.id ? paymentCardGradient.iconColorSelected : paymentCardGradient.iconColorUnselected}`} />
            </div>
            <div className="flex-1 text-left">
              <span className="font-medium text-white capitalize">{pm.brand}</span>
              <span className="text-white/50 font-mono text-sm ml-2">•••• {pm.last4}</span>
            </div>
            {selectedId === pm.id && (
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${paymentCardGradient.checkBadge}`}>
                <Check className={`w-3.5 h-3.5 ${paymentCardGradient.checkIcon}`} strokeWidth={2.5} />
              </div>
            )}
          </button>
        ))}
        <button
          type="button"
          onClick={onAddNew}
          className={paymentCardGradient.addNewCard}
        >
          <div className={paymentCardGradient.addNewCardIcon}>
            <CreditCard className="w-5 h-5 text-white/40" />
          </div>
          Add new card
        </button>
      </div>
    </div>
  );
}

function AddCardInline({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(reserveUrl('create-setup-intent'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) }
      });

      if (confirmError) throw new Error(confirmError.message);

      onSuccess(setupIntent.payment_method);
      toast.success('Card added');
    } catch (err) {
      toast.error(err.message || 'Failed to add card');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <p className="text-white/70 text-xs font-normal uppercase tracking-wider mb-4">Add Card</p>
      <div className={`${paymentCardGradient.cardElementWrapper} mb-4`}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#ffffff',
                '::placeholder': { color: 'rgba(255,255,255,0.4)' },
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: '500'
              },
              invalid: { color: '#f87171', iconColor: '#f87171' }
            },
            hidePostalCode: true
          }}
        />
      </div>
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={isSubmitting || !stripe}
          className="flex-1 bg-white text-black hover:bg-white/90 rounded-full"
        >
          {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Card'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="border border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-full"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function FlightSegmentCard({ slice, isReturn, bookingOption, searchCabinClass }) {
  const segment = slice.segments[0];
  const lastSegment = slice.segments[slice.segments.length - 1];
  const duration = slice.duration; // "PT1H10M"
  const marketingCarrier = segment.marketing_carrier;
  const stops = slice.segments.length - 1;
  
  // Cabin class: prefer the booking option title, then segment-level data, then search params
  const fareOption = isReturn 
    ? (bookingOption?.returning || bookingOption?.together)
    : (bookingOption?.departing || bookingOption?.together);
  const fareClassName = fareOption?.option_title;
  
  const segmentCabin = segment.passengers_cabin_class_marketing_name || 
                       segment.cabin_class_marketing_name || 
                       segment.cabin_class;
  
  const cabinDisplay = fareClassName || segmentCabin || searchCabinClass || null;
  // Capitalize first letter
  const formatCabin = (str) => {
    if (!str) return null;
    return str.replace(/\b\w/g, c => c.toUpperCase());
  };
                     
  const aircraft = segment.aircraft?.name;
  const flightNumber = segment.marketing_carrier_flight_number;
  const operatingCarrier = segment.operating_carrier?.name;
  const isCodeshare = operatingCarrier && operatingCarrier !== marketingCarrier?.name;

  // Format duration
  const formatDuration = (isoDuration) => {
    if (!isoDuration) return '';
    if (typeof isoDuration === 'number') {
      const hours = Math.floor(isoDuration / 60);
      const minutes = isoDuration % 60;
      return `${hours} hr ${minutes} min`;
    }
    if (typeof isoDuration !== 'string') return '';
    const matches = isoDuration.match(/PT(\d+H)?(\d+M)?/);
    if (!matches) return '';
    const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
    const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
    return `${hours} hr ${minutes} min`;
  };

  const formatTime = (isoString) => {
    return format(parseISO(isoString), 'h:mm a');
  };

  const formatDate = (isoString) => {
    return format(parseISO(isoString), 'EEE, MMM d');
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-3">
      {/* Date Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-white/40" />
          <span className="text-white/60 text-xs font-normal uppercase tracking-wide">
            {isReturn ? 'Return' : 'Departing'} • {formatDate(segment.departing_at)}
          </span>
        </div>
        {stops > 0 && (
          <span className="text-brand-400 text-[10px] uppercase tracking-wide font-normal bg-brand-400/10 px-2 py-0.5 rounded">
            {stops} stop{stops > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center p-0.5 overflow-hidden mt-1">
            {marketingCarrier?.logo_symbol_url ? (
              <img 
                src={marketingCarrier.logo_symbol_url} 
                alt={marketingCarrier.name} 
                className="w-full h-full object-contain"
              />
            ) : (
              <PlaneTakeoff className="w-6 h-6 text-white/40" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-white text-base font-medium">
                {formatTime(segment.departing_at)} – {formatTime(lastSegment.arriving_at)}
              </span>
            </div>
            {/* Line 1: Airline / Flight Number */}
            <div className="text-white/40 text-xs font-normal">
              {marketingCarrier?.name}
              {flightNumber && ` ${flightNumber}`}
            </div>
            {/* Line 2: Aircraft • Cabin Class */}
            <div className="text-white/40 text-xs font-normal">
              {aircraft || 'Aircraft'}
              {formatCabin(cabinDisplay) && ` • ${formatCabin(cabinDisplay)}`}
            </div>
            {/* Line 3: Stops */}
            <div className="text-white/40 text-xs font-normal">
              {stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}
            </div>
            
            {isCodeshare && (
              <div className="text-white/30 text-[10px] font-normal mt-0.5">
                Operated by {operatingCarrier}
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-white/40 text-xs font-normal uppercase tracking-wider mb-1">
            {segment.origin.iata_code}–{lastSegment.destination.iata_code}
          </div>
          <div className="text-white text-sm font-normal">
            {formatDuration(duration)}
          </div>
        </div>
      </div>

      {/* Layovers for connecting flights */}
      {stops > 0 && slice.segments.length > 1 && (
        <div className="pt-3 border-t border-white/5 space-y-2">
          {slice.segments.map((seg, idx) => {
            const nextSeg = slice.segments[idx + 1];
            if (!nextSeg) return null;
            return (
              <div key={idx} className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-2 text-brand-400/80">
                  <Clock className="w-3 h-3" />
                  <span>
                    Layover in {seg.destination.iata_code} • {formatDuration(nextSeg.layover_duration || "PT0M")}
                  </span>
                </div>
                {seg.aircraft?.name && (
                  <span className="text-white/30">{seg.aircraft.name}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Defera Reserve was discontinued for new users. The implementation below
// is preserved verbatim (in case Reserve is brought back) but is no longer
// reachable — the default export at the bottom of this file redirects every
// visit to /. Keeping the original component intact means re-enabling
// Reserve checkout is a one-line change to the default export.
// eslint-disable-next-line no-unused-vars
function ReserveCheckoutImpl() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedFlight, totalAmountCents: passedTotal } = location.state || {};

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  // Available Reserve balance the customer can apply to this trip = total balance minus
  // anything already allocated to other active trips. Loaded once on mount and refreshed
  // after a failed/successful create-trip so the calculator stays accurate.
  const [availableBalanceCents, setAvailableBalanceCents] = useState(0);
  const [confirmError, setConfirmError] = useState(null);

  // Checkout trouble routes to Tommy, so the Reserve checkout has a way to
  // reach the concierge.
  const supportHref = useMemo(
    () => buildTommyUrl({ message: "I'm trying to set up a Defera Reserve plan but it isn't going through." }),
    [],
  );

  const checkoutFaqs = [
    { question: 'What is Defera Reserve?', answer: 'Defera Reserve lets you pay toward a trip over time before you ticket. Make payments on your schedule. Ticket when your plan is fully paid or top off to expedite. If the price goes up, switch flights. If it goes down, you keep the savings.' },
    { question: 'Is my fare locked when I start paying?', answer: 'No, prices remain live until you ticket. Reserve gives you flexibility without the risk. You\'re making payments toward a trip, not committing to a price.' },
    { question: 'What happens if the price goes down?', answer: 'You win! If the fare drops before you ticket, you only pay the lower price. The difference remains in your Reserve balance for future travel.' },
    { question: 'Can I switch flights after I start paying?', answer: 'Absolutely. Until you ticket, your Reserve balance can be applied to any flight on Defera. Changed your mind? Found a better deal? Just switch—no fees, no penalties.' },
    { question: 'How do payments work?', answer: 'Your trip total is split into equal payments—no large deposits. Your first payment is due today, and the rest follow your chosen schedule (weekly, biweekly, or monthly). All payments wrap up 4 weeks before departure.' },
    { question: 'When do I add passenger details?', answer: 'Passengers can be selected once you\'re ready to ticket. There\'s no need to lock in passenger details now—add them when you complete your booking.' }
  ];

  const baseFareCents = passedTotal || Math.round((parseFloat(selectedFlight?.total_amount) || 0) * 100);
  const serviceFeeCents = Math.round(baseFareCents * 0.11);
  const totalCents = baseFareCents + serviceFeeCents;

  // Derive route info
  const firstSlice = selectedFlight?.slices?.[0];
  const departureDate = firstSlice?.segments?.[0]?.departing_at;

  // Check if trip qualifies for Reserve (departure >= 2 months out)
  const isEligible = useMemo(() => {
    if (!departureDate) return false;
    const dep = new Date(departureDate);
    const minDate = new Date();
    minDate.setMonth(minDate.getMonth() + 2);
    return dep > minDate;
  }, [departureDate]);

  useEffect(() => {
    if (!selectedFlight) {
      navigate('/search');
      return;
    }
    if (!isEligible) {
      toast.error('This trip departs too soon for Reserve. Departure must be at least 2 months away.');
      navigate(-1);
      return;
    }
    loadUserAndMethods();
  }, []);

  // ... (loadUserAndMethods, handleCardAdded, handleConfirm remain same) ...
  const loadUserAndMethods = async () => {
    try {
      const u = await User.getCurrentUser();
      if (!u) {
        sessionStorage.setItem('auth_redirect', '/reserve/checkout');
        navigate('/home?auth_mode=login');
        return;
      }
      setUser(u);

      // Ensure Stripe customer
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(reserveUrl('ensure-customer'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        })
      });

      // Fetch payment methods
      const pmRes = await fetch(reserveUrl('payment-methods'), {
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
      });
      if (pmRes.ok) {
        const pmData = await pmRes.json();
        setPaymentMethods(pmData.payment_methods || []);
        const defaultPm = pmData.payment_methods?.find(p => p.is_default);
        if (defaultPm) setSelectedPaymentMethod(defaultPm.id);
        else if (pmData.payment_methods?.length > 0) setSelectedPaymentMethod(pmData.payment_methods[0].id);
      }

      // Fetch Reserve balance + allocations so the calculator can offer to apply existing
      // credit toward this trip. Failure is non-fatal — the form still works without it.
      try {
        const balRes = await fetch('/api/reserve/balance', {
          headers: mergeReserveActAsHeaders({ Authorization: `Bearer ${session.access_token}` }),
        });
        if (balRes.ok) {
          const balData = await balRes.json();
          const balance = Number(balData.balance_cents) || 0;
          const allocated = Number(balData.total_allocated_cents) || 0;
          setAvailableBalanceCents(Math.max(0, balance - allocated));
        }
      } catch {
        // ignore — toggle simply won't appear; user can still proceed without it
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardAdded = async (paymentMethodId) => {
    setShowAddCard(false);
    // Refresh payment methods
    const { data: { session } } = await supabase.auth.getSession();
    const pmRes = await fetch(reserveUrl('payment-methods'), {
      headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
    });
    if (pmRes.ok) {
      const pmData = await pmRes.json();
      setPaymentMethods(pmData.payment_methods || []);
      setSelectedPaymentMethod(paymentMethodId);
    }
  };

  const handleConfirm = async (frequency, schedule, options = {}) => {
    if (!selectedPaymentMethod) {
      toast.error('Please select or add a payment method');
      return;
    }

    // Correlate started → succeeded/failed events so support can find a single attempt's
    // breadcrumb trail in user_activity_log even when nothing reached Stripe.
    const attemptId = newReserveAttemptId();
    const applyBalanceCents = Number.isInteger(options?.applyBalanceCents) && options.applyBalanceCents > 0
      ? Math.min(options.applyBalanceCents, availableBalanceCents, Math.max(0, totalCents - 100))
      : 0;

    setConfirmError(null);
    setIsProcessing(true);

    // Re-derive necessary fields for API (also used in the breadcrumb).
    const firstSeg = selectedFlight.slices[0].segments[0];
    const lastSlice = selectedFlight.slices[selectedFlight.slices.length - 1];
    const lastSeg = lastSlice.segments[lastSlice.segments.length - 1];
    const departureCity = getCityName(firstSeg.origin?.iata_code, firstSeg.origin);
    const arrivalCity = getCityName(firstSeg.destination?.iata_code, lastSeg.destination);
    const returnDate = selectedFlight.slices.length > 1 ? lastSlice.segments[0].departing_at : null;
    const marketingAirline = firstSeg.marketing_carrier?.name || '';
    // I74BF5: prefer the airline's actual fare brand from the FlightOptions
    // selection (e.g. "Level") over the SerpAPI segment fields, which are
    // routinely empty for synthesized offers — that's why pre-fix Reserve trips
    // ended up with a NULL `cabin_class` and SliceCard rendered no fare label
    // at all. Fall through to segment marketing names → segment cabin enum so
    // legacy Duffel offers without bookingOption still populate the column.
    const cabinClass = resolveMarketingFareNameFromOffer(
      selectedFlight,
      firstSeg.passengers_cabin_class_marketing_name || firstSeg.cabin_class_marketing_name || firstSeg.cabin_class || '',
    );
    const tripType = selectedFlight.slices.length > 1 ? 'round_trip' : 'one_way';

    const baseBreadcrumb = {
      attempt_id: attemptId,
      payment_frequency: frequency,
      total_amount_cents: totalCents,
      base_fare_cents: baseFareCents,
      service_fee_cents: serviceFeeCents,
      apply_balance_cents: applyBalanceCents,
      departure_date: departureDate,
      route: `${firstSeg.origin?.iata_code || '???'}-${lastSeg.destination?.iata_code || '???'}`,
      has_payment_method: !!selectedPaymentMethod,
      num_payments_planned: schedule?.numPayments || null,
    };

    // Fire-and-forget breadcrumb BEFORE the request so we still have a record if the fetch
    // itself fails (network drop, edge timeout, CORS) — the silent-failure case Nevaeh hit.
    logReserveActivity('reserve_trip_attempt_started', baseBreadcrumb);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(reserveUrl('create-trip'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({
          flight_data: selectedFlight,
          total_amount_cents: totalCents,
          base_fare_cents: baseFareCents,
          service_fee_cents: serviceFeeCents,
          payment_frequency: frequency,
          payment_method_id: selectedPaymentMethod,
          // Optional: opt-in offset of the subscription plan by existing Reserve credit.
          // The server validates this against the user's reconciled balance + existing
          // allocations and is the source of truth — never trust the client-side cap.
          ...(applyBalanceCents > 0 && { apply_balance_cents: applyBalanceCents }),
          departure_city: departureCity,
          arrival_city: arrivalCity,
          departure_date: departureDate,
          return_date: returnDate,
          marketing_airline: marketingAirline,
          cabin_class: cabinClass,
          trip_type: tripType
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errMsg = data.error || `Failed to create trip (HTTP ${res.status})`;
        const err = new Error(errMsg);
        err.code = data.code || `http_${res.status}`;
        err.declineCode = data.decline_code || null;
        throw err;
      }

      logReserveActivity('reserve_trip_attempt_succeeded', {
        ...baseBreadcrumb,
        trip_id: data?.trip?.id || null,
        subscription_id: data?.subscription_id || null,
        first_payment_recorded: !!data?.first_payment_recorded,
      });

      toast.success('Reserve plan created!');
      navigate('/app/reserve');
    } catch (err) {
      const errorMessage = err?.message || 'Something went wrong setting up your plan.';
      logReserveActivity('reserve_trip_attempt_failed', {
        ...baseBreadcrumb,
        error_message: String(errorMessage).slice(0, 500),
        error_code: err?.code || null,
        decline_code: err?.declineCode || null,
        // `name` distinguishes TypeError (network) from generic Error (server-returned)
        // and from AbortError — meaningful when the breadcrumb is the only signal we get.
        error_name: err?.name || 'Error',
      });
      setConfirmError(errorMessage);
      // Refresh balance in case the failure was related to a stale view of allocations
      // (e.g. the user opened another tab and applied their balance there).
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const balRes = await fetch('/api/reserve/balance', {
            headers: mergeReserveActAsHeaders({ Authorization: `Bearer ${session.access_token}` }),
          });
          if (balRes.ok) {
            const balData = await balRes.json();
            const balance = Number(balData.balance_cents) || 0;
            const allocated = Number(balData.total_allocated_cents) || 0;
            setAvailableBalanceCents(Math.max(0, balance - allocated));
          }
        }
      } catch { /* non-fatal */ }
    } finally {
      setIsProcessing(false);
    }
  };

  // Wired into the inline error block's "Try again" button. The actual frequency/schedule
  // come from the calculator state; we just clear the error so the disabled-state recomputes
  // and let the user re-press the primary CTA. (Re-running handleConfirm directly would need
  // the latest schedule object, which only the calculator has.)
  const handleRetryFromError = () => {
    setConfirmError(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!selectedFlight) return null;

  const formatCurrency = (cents) => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <Helmet>
        <title>Reserve Checkout | Defera</title>
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/5">
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 h-16 flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-3 text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-normal">Back</span>
            </button>
            <div className="flex-1" />
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 pt-24 pb-16">
          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="font-mono text-xs text-white/40 tracking-widest uppercase">Defera Reserve</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white">
              Reserve Checkout
            </h1>
            <p className="text-white/60 text-sm font-normal mt-2 max-w-xl">
              Defera Reserve lets you pay toward a trip over time before you ticket. Split into equal payments—no large deposits, no interest. Prices stay live until you book; ticket when fully paid or top up anytime to expedite.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8">

            {/* Left Column: Flight Details — top padding matches right column card (p-6 md:p-8) so both columns align */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="pt-6 md:pt-8"
              >
                <p className="text-white/40 text-xs tracking-widest uppercase mb-4">Your Trip</p>

                {/* Flight cards */}
                <div className="space-y-0">
                  {selectedFlight.slices?.map((slice, i) => (
                    <FlightSegmentCard
                      key={i}
                      slice={slice}
                      isReturn={i > 0}
                      bookingOption={selectedFlight.bookingOption}
                      searchCabinClass={selectedFlight.slices?.[0]?.segments?.[0]?.cabin_class}
                    />
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-center gap-2">
                  <span className="text-white/60 font-light text-lg">Base Fare:</span>
                  <span className="text-white font-medium text-2xl">${formatCurrency(baseFareCents)}</span>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Calculator + Payment */}
            <div className="lg:col-span-3">
              {/* ... (Existing Calculator + Payment UI) ... */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="space-y-6"
              >
                {/* Payment Calculator + Method in one card */}
                <Card className="bg-white/[0.03] border-white/10 rounded-3xl overflow-hidden">
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <PaymentCalculator
                      totalCents={totalCents}
                      baseFareCents={baseFareCents}
                      serviceFeeCents={serviceFeeCents}
                      departureDate={departureDate}
                      onConfirm={handleConfirm}
                      isProcessing={isProcessing}
                      availableBalanceCents={availableBalanceCents}
                      errorMessage={confirmError}
                      onRetry={handleRetryFromError}
                      supportHref={supportHref}
                    >
                      <div className="mb-6">
                        {showAddCard ? (
                          <Elements stripe={stripePromise}>
                            <AddCardInline
                              onSuccess={handleCardAdded}
                              onCancel={() => setShowAddCard(false)}
                            />
                          </Elements>
                        ) : (
                          <PaymentMethodSelector
                            paymentMethods={paymentMethods}
                            selectedId={selectedPaymentMethod}
                            onSelect={setSelectedPaymentMethod}
                            onAddNew={() => setShowAddCard(true)}
                          />
                        )}
                      </div>
                    </PaymentCalculator>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>

          {/* FAQs */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-16 sm:mt-20 pt-10 sm:pt-12 border-t border-white/10"
          >
            <h2 className="text-white/80 text-lg sm:text-xl font-light mb-6 sm:mb-8">Frequently asked questions</h2>
            <div className="space-y-2 sm:space-y-3">
              {checkoutFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-4 py-4 sm:px-5 sm:py-5 flex items-center justify-between gap-3 text-left hover:bg-white/5 transition-colors min-h-[48px] sm:min-h-0"
                  >
                    <span className="text-white/90 text-sm sm:text-base font-normal pr-2">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 text-white/50 transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0">
                      <p className="text-white/60 text-sm sm:text-[15px] font-normal leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
}

/**
 * Defera Reserve was discontinued for new users, so we no longer accept new
 * Reserve plans from anyone — even existing customers with a wallet balance
 * manage their plans from /app/reserve. The full ReserveCheckoutImpl above
 * is preserved (in case Reserve is brought back); this exported component
 * is a thin redirect-only wrapper that bounces every visit to /.
 */
export default function ReserveCheckout() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);
  return <div className="min-h-screen bg-black" />;
}
